import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BrCard, BrButton } from '@govbr-ds/react-components'
import { executeTestCaseAsync } from '../services/api'
import {
  getExecutions,
  getMetricsSummary,
  getTestCases,
  subscribeToExecutions,
  ExecutionWithId,
  MetricsSummary,
  TestCaseWithId,
} from '../services/firestore-proxy'

// Real TST test cases (fetched from DataJud)
import realTestCases from '../../data/real_tst_test_cases.json'


interface ExecutionSummary {
  total: number
  success: number
  failed: number
  pending: number
}

interface BatchProgress {
  current: number
  total: number
  startedAt: Date | null
}

export function DashboardPage() {
  const [loading, setLoading] = useState(false)
  const [metrics, setMetrics] = useState<MetricsSummary | null>(null)
  const [batchProgress, setBatchProgress] = useState<BatchProgress>({
    current: 0,
    total: 0,
    startedAt: null,
  })
  const [cycleStartTime, setCycleStartTime] = useState<Date | null>(null)
  const [summary, setSummary] = useState<ExecutionSummary>({
    total: 0,
    success: 0,
    failed: 0,
    pending: 0,
  })

  // Ref to track cancellation state (works across async callbacks)
  const cancelledRef = useRef(false)
  const { t } = useTranslation()

  useEffect(() => {
    // Subscribe to executions with polling
    const subscription = subscribeToExecutions(
      (executions: ExecutionWithId[]) => {
        // Filter by cycle start time if in a batch cycle
        const filteredExecs = cycleStartTime
          ? executions.filter(e => {
            const execTime = new Date(e.started_at || e.timestamp || 0)
            return execTime >= cycleStartTime
          })
          : executions

        const newSummary = filteredExecs.reduce<ExecutionSummary>(
          (acc, exec) => {
            acc.total++
            if (exec.status === 'success') acc.success++
            else if (exec.status === 'failed' || exec.status === 'error') acc.failed++
            else acc.pending++
            return acc
          },
          { total: 0, success: 0, failed: 0, pending: 0 }
        )
        setSummary(newSummary)
      },
      { limit: 100, pollInterval: 3000 } // Faster polling during batch
    )

    // Load metrics summary
    loadMetrics()

    return () => {
      subscription.unsubscribe()
    }
  }, [cycleStartTime]) // Re-subscribe when cycle starts

  const loadMetrics = async () => {
    const result = await getMetricsSummary()
    if (result.success && result.data && result.data.success_rate !== undefined) {
      setMetrics(result.data)
    } else {
      setMetrics(null)
    }
  }

  // Manual refresh of executions summary
  const loadExecutions = async () => {
    const result = await getExecutions({ limit: 100, orderBy: 'created_at_desc' })
    if (result.success && result.data) {
      const newSummary = result.data.reduce<ExecutionSummary>(
        (acc, exec) => {
          acc.total++
          if (exec.status === 'success') acc.success++
          else if (exec.status === 'failed' || exec.status === 'error') acc.failed++
          else acc.pending++
          return acc
        },
        { total: 0, success: 0, failed: 0, pending: 0 }
      )
      setSummary(newSummary)
    }
  }

  // Helper function to execute in chunks to avoid server overload
  const executeInChunks = async (
    testCases: Awaited<ReturnType<typeof getTestCases>>['data'],
    chunkSize = 1,
    onProgress?: (current: number, total: number) => void
  ) => {
    if (!testCases) return { success: 0, failed: 0, cancelled: false }

    let success = 0
    let failed = 0
    const total = testCases.length

    for (let i = 0; i < testCases.length; i += chunkSize) {
      // Check if cancelled before starting next chunk
      if (cancelledRef.current) {

        return { success, failed, cancelled: true }
      }

      const chunk = testCases.slice(i, i + chunkSize)
      const promises = chunk.map(tc => executeTestCaseAsync(tc))
      const results = await Promise.allSettled(promises)

      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value.success) success++
        else failed++
      })

      // Update progress
      const current = Math.min(i + chunkSize, total)
      onProgress?.(current, total)

      // Delay between chunks to avoid LLM rate limiting
      // 3 seconds is safe with gemini-3-flash-preview (10K RPD limit)
      if (i + chunkSize < testCases.length) {
        await new Promise(resolve => setTimeout(resolve, 3000))
      }
    }

    return { success, failed, cancelled: false }
  }

  const handleRunBatch = async () => {
    // Reset cancellation flag
    cancelledRef.current = false
    setLoading(true)
    // Set cycle start time for filtering
    const startTime = new Date()
    setCycleStartTime(startTime)
    setBatchProgress({ current: 0, total: 0, startedAt: startTime })

    try {
      // Use real TST test cases from DataJud
      const testCasesToExecute = (realTestCases.test_cases as unknown as TestCaseWithId[]).map((tc) => ({
        ...tc,
        id: tc.case_id,
      }))


      if (testCasesToExecute.length > 0) {
        const totalTests = testCasesToExecute.length
        setBatchProgress({ current: 0, total: totalTests, startedAt: startTime })

        // Execute one at a time with progress callback
        const { cancelled } = await executeInChunks(
          testCasesToExecute,
          1,
          (current, total) => setBatchProgress({ current, total, startedAt: startTime })
        )
        if (cancelled) {
          // Batch cancelled by user
        } else {
          // Batch completed successfully
        }
      } else {
        // No tests to execute
      }
      // Refresh executions summary manually
      await loadExecutions()
      await loadMetrics()
    } catch (error) {
      console.error('Error running batch:', error)
    } finally {
      setLoading(false)
      // Keep cycle start time so we continue showing only this cycle's executions
    }
  }

  const handleResetCycle = () => {
    setCycleStartTime(null)
    setBatchProgress({ current: 0, total: 0, startedAt: null })
  }

  const handleCancelBatch = () => {
    cancelledRef.current = true

  }

  return (
    <div className="br-container px-0">
      <div className="d-flex flex-column gap-6">
        <div className="d-flex flex-column sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-80">{t('dashboard.title')}</h1>
            {cycleStartTime && (
              <p className="text-sm text-gray-60 mt-1">
                {t('dashboard.cycleStarted')} {cycleStartTime.toLocaleTimeString()}
                <button
                  onClick={handleResetCycle}
                  className="ml-2 text-primary-default hover-underline border-0 bg-transparent cursor-pointer"
                >
                  {t('common.viewAll')}
                </button>
              </p>
            )}
          </div>
          <div className="d-flex align-items-center gap-4 mt-4 sm:mt-0">
            {loading && batchProgress.total > 0 && (
              <span className="text-sm font-medium text-primary-default">
                {t('common.sending')}: {batchProgress.current}/{batchProgress.total}
              </span>
            )}
            {loading ? (
              <>
                <BrButton primary loading onClick={handleRunBatch}>
                  {t('dashboard.executing')}
                </BrButton>
                <BrButton
                  onClick={handleCancelBatch}
                  className="bg-danger-vivid hover-bg-danger text-pure-0"
                >
                  {t('common.cancel')}
                </BrButton>
              </>
            ) : (
              <BrButton primary onClick={handleRunBatch}>
                {t('dashboard.executeAll')}
              </BrButton>
            )}
          </div>
        </div>


        {/* Progress Bar during batch */}
        {loading && batchProgress.total > 0 && (
          <div className="w-100 bg-gray-20 rounded-full" style={{ height: '0.625rem' }}>
            <div
              className="bg-primary-default rounded-full transition-all duration-300"
              style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%`, height: '0.625rem' }}
            />
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <BrCard className="text-center">
            <p className="text-3xl font-bold text-primary-default">{summary.total}</p>
            <p className="text-sm text-gray-60">{t('dashboard.totalExecutions')}</p>
          </BrCard>
          <BrCard className="text-center">
            <p className="text-3xl font-bold text-success">{summary.success}</p>
            <p className="text-sm text-gray-60">{t('dashboard.success')}</p>
          </BrCard>
          <BrCard className="text-center">
            <p className="text-3xl font-bold text-danger">{summary.failed}</p>
            <p className="text-sm text-gray-60">{t('dashboard.failed')}</p>
          </BrCard>
        </div>

        {/* Metrics */}
        {metrics && (
          <BrCard>
            <div className="card-header">
              <span className="title">{t('dashboard.metrics.title')}</span>
            </div>
            <div className="card-content">
              <div className="d-flex flex-column md:flex-row gap-6">
                <div className="flex-1">
                  <p className="text-2xl font-bold text-primary-default">
                    {metrics.success_rate.toFixed(1)}%
                  </p>
                  <p className="text-sm text-gray-60">{t('dashboard.metrics.successRate')}</p>
                </div>
                <div className="flex-1">
                  <p className="text-2xl font-bold text-primary-default">
                    {metrics.avg_tads.toFixed(1)}%
                  </p>
                  <p className="text-sm text-gray-60">{t('dashboard.metrics.avgTads')}</p>
                </div>
                <div className="flex-1">
                  <p className="text-2xl font-bold text-primary-default">
                    {metrics.avg_execution_time.toFixed(0)}ms
                  </p>
                  <p className="text-sm text-gray-60">{t('dashboard.metrics.avgTime')}</p>
                </div>
              </div>
            </div>
          </BrCard>
        )}

        {/* Metrics by Persona */}
        {metrics?.by_persona && (
          <BrCard>
            <div className="card-header">
              <span className="title">{t('dashboard.byPersona.title')}</span>
            </div>
            <div className="card-content">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">{t('dashboard.byPersona.persona')}</th>
                      <th className="text-right py-2">{t('dashboard.byPersona.successRate')}</th>
                      <th className="text-right py-2">{t('dashboard.byPersona.avgTads')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(metrics.by_persona).map(([persona, data]) => (
                      <tr key={persona} className="border-b">
                        <td className="py-2">{t(`personas.${persona}`, persona)}</td>
                        <td className="text-right py-2">
                          {data.success_rate.toFixed(1)}%
                        </td>
                        <td className="text-right py-2">
                          {data.avg_tads.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </BrCard>
        )}
      </div>
    </div>
  )
}
